import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import * as sched from 'sched';
import { useWatchTarget } from '@/hooks/useWatchlist';

interface WatchButtonProps {
  workspaceId: string | undefined;
  target: sched.WatchTarget;
  /** Optional descriptive label next to the icon. Defaults to true. */
  showLabel?: boolean;
}

/**
 * Toggle watching a Task / Outcome / Team on the current user's watchlist.
 * Renders as a pill-style button matching the style of the completion toggle
 * and other header actions on detail pages.
 */
const WatchButton: React.FC<WatchButtonProps> = ({ workspaceId, target, showLabel = true }) => {
  const { watching, startWatching, stopWatching } = useWatchTarget(workspaceId, target);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onToggle = async () => {
    if (!workspaceId || watching === null) return;
    setPending(true);
    setErrorMessage(null);
    try {
      if (watching) {
        await stopWatching();
      } else {
        await startWatching();
      }
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Failed to update watchlist');
    } finally {
      setPending(false);
    }
  };

  // Show neutral until the initial GET resolves so we don't flash "Watch"
  // and then immediately switch to "Watching".
  const stateKnown = watching !== null;
  const isWatching = watching === true;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggle}
        disabled={pending || !workspaceId || !stateKnown}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-sm transition-colors border ${
          isWatching
            ? 'bg-cm-accent-teal/15 text-cm-accent-teal border-cm-accent-teal/40 hover:bg-cm-accent-teal/25'
            : 'bg-cm-bg-deep text-cm-text-secondary border-cm-border-subtle hover:border-cm-accent-teal hover:text-cm-accent-teal'
        } ${pending || !stateKnown ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
        aria-label={isWatching ? 'Stop watching' : 'Watch'}
        aria-pressed={isWatching}
      >
        {isWatching ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
        {showLabel && <span>{isWatching ? 'Watching' : 'Watch'}</span>}
      </button>
      {errorMessage && (
        <span className="text-xs text-red-400" role="alert">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default WatchButton;
